import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NotificationService } from '../../../core/services/notification.service';

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="toast-container" aria-live="polite">
      @for (notification of notificationService.notifications(); track notification.id) {
        <div
          class="toast"
          [class]="'toast--' + notification.type"
          (click)="dismiss(notification.id)"
        >
          <span class="toast__icon">{{ getIcon(notification.type) }}</span>
          <span class="toast__message">{{ notification.message }}</span>
          <button
            type="button"
            class="toast__close"
            (click)="dismiss(notification.id); $event.stopPropagation()"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      }
    </div>
  `,
  styles: [`
    .toast-container {
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 9999;
      display: flex;
      flex-direction: column;
      gap: 10px;
      max-width: min(420px, calc(100vw - 40px));
    }

    .toast {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 14px 16px;
      border-radius: 12px;
      box-shadow: 0 10px 40px rgba(15, 23, 42, 0.15);
      cursor: pointer;
      animation: slideIn 0.3s ease;
      border: 1px solid transparent;
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateX(24px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }

    .toast__icon {
      font-size: 1rem;
      font-weight: 700;
      flex-shrink: 0;
    }

    .toast__message {
      flex: 1;
      font-size: 0.875rem;
      line-height: 1.4;
    }

    .toast__close {
      background: none;
      border: none;
      font-size: 1.25rem;
      line-height: 1;
      cursor: pointer;
      opacity: 0.6;
      padding: 0 4px;
      color: inherit;
    }

    .toast__close:hover {
      opacity: 1;
    }

    .toast--success {
      background: #ecfdf5;
      border-color: #6ee7b7;
      color: #065f46;
    }

    .toast--error {
      background: #fef2f2;
      border-color: #fca5a5;
      color: #991b1b;
    }

    .toast--info {
      background: #eff6ff;
      border-color: #93c5fd;
      color: #1e40af;
    }
  `],
})
export class ToastComponent {
  readonly notificationService = inject(NotificationService);

  dismiss(id: number): void {
    this.notificationService.dismiss(id);
  }

  getIcon(type: string): string {
    const icons: Record<string, string> = {
      success: '✓',
      error: '✕',
      info: 'ℹ',
    };
    return icons[type] ?? 'ℹ';
  }
}
